import { useCallback, useEffect, useRef } from 'react';
import type { FlatListProps } from 'react-native';
import { useLogAnalyticsEvent } from 'shared/src/hooks/useLogAnalyticsEvent';
import { bridge, isNotNil } from 'shared/src/utils';
import { AdAction } from 'shared/src/utils/eventLogger/adLogger/types';
import type { FolderType } from '../constants';
import type { FolderDetailRow } from '../types';
import type { FolderGoodsCardItem } from '../utils/convertFolderGoodsItemToGoodsData';

interface UseLogDisplayGoodsParams {
  /** 카테고리/정렬/배송 필터 변경 등으로 리스트가 갱신되면 중복 방지 Set 초기화 */
  resetKey: string;
  folderType: FolderType;
}

export const useLogDisplayGoods = ({
  resetKey,
  folderType,
}: UseLogDisplayGoodsParams) => {
  const loggedSnoRef = useRef<Set<number>>(new Set());
  const logAnalyticsEvent = useLogAnalyticsEvent();

  useEffect(() => {
    loggedSnoRef.current = new Set();
  }, [resetKey]);

  const logEntry = useCallback(
    (goods: FolderGoodsCardItem) => {
      if (loggedSnoRef.current.has(goods.sno)) return;
      loggedSnoRef.current.add(goods.sno);

      logAnalyticsEvent({
        name: 'DISPLAY_GOODS',
        analytics: {
          ...goods.logging.analytics,
          FOLDER_TYPE: folderType,
        },
        ably_only_log: goods.logging.ably_only_log,
      });

      if (isNotNil(goods.ad)) {
        bridge.logAdEvent({ action: AdAction.DISPLAY, ad: goods.ad });
      }
    },
    [logAnalyticsEvent, folderType],
  );

  return useCallback<
    NonNullable<FlatListProps<FolderDetailRow>['onViewableItemsChanged']>
  >(
    ({ viewableItems }) => {
      viewableItems.forEach(({ item, isViewable }) => {
        if (!isViewable || item.type !== 'goodsRow') return;
        item.items.forEach(logEntry);
      });
    },
    [logEntry],
  );
};
